/**
 * BlogQuestions — /blog/questions
 *
 * Community Q&A powered by an AI assistant with DB caching:
 *   • Reader asks a question → normalized-hash lookup in Mongo first
 *   • Cache miss → single Claude Sonnet call → persisted → free forever
 *   • All Q&A are public, filterable by topic, thumbs-up upvote-able
 *
 * Design guardrails: cream/teal/Lora — same aesthetic as the rest of
 * SocialPulse.blog. No fat SocialPulse chrome.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Send, Loader2, ThumbsUp, ArrowRight, MessageCircle, X, PenLine,
} from "lucide-react";
import ListenToAnswer from "@/components/publisher/ListenToAnswer";
import TransliterateInput from "@/components/TransliterateInput";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

const TOPIC_CHIPS = [
  { slug: "",              label: "All topics" },
  { slug: "health",        label: "Health" },
  { slug: "education",     label: "Education" },
  { slug: "finance",       label: "Finance" },
  { slug: "food",          label: "Food" },
  { slug: "travel",        label: "Travel" },
  { slug: "nature",        label: "Nature" },
  { slug: "farmers",       label: "Farmers" },
  { slug: "family-trees",  label: "Family Trees" },
  { slug: "memorials",     label: "Memorials" },
  { slug: "history",       label: "History" },
  { slug: "faith",         label: "Faith" },
];

export default function BlogQuestions() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState("");
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState(null);
  const [freshAnswer, setFreshAnswer] = useState(null);
  const [list, setList] = useState([]);
  const [sort, setSort] = useState("popular");
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${BASE}/questions`, window.location.origin);
      url.searchParams.set("limit", "30");
      url.searchParams.set("sort", sort);
      if (topic) url.searchParams.set("topic", topic);
      const r = await fetch(url.toString().replace(window.location.origin, ""));
      const d = await r.json();
      setList(d.questions || []);
    } catch { setList([]); }
    setLoading(false);
  }, [topic, sort]);

  useEffect(() => { load(); }, [load]);

  const ask = async (e) => {
    e.preventDefault();
    const question = q.trim();
    if (question.length < 6) return;
    setAsking(true);
    setAskError(null);
    setFreshAnswer(null);
    try {
      const r = await fetch(`${BASE}/questions/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, topic: topic || null }),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setFreshAnswer(d);
      setQ("");
      // Refresh the list so the new (or cache-hit) row surfaces at top
      load();
    } catch (err) {
      setAskError(err.message || "Ask failed — please try again.");
    } finally {
      setAsking(false);
    }
  };

  const upvote = async (item) => {
    try {
      const r = await fetch(`${BASE}/questions/${item.id}/vote`, { method: "POST" });
      const d = await r.json();
      setList((prev) => prev.map((x) => x.id === item.id ? { ...x, helpful_votes: d.helpful_votes } : x));
    } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }} data-testid="blog-questions-page">
      {/* Minimal blog header */}
      <header className="border-b" style={{ borderColor: "rgba(15,23,42,0.06)", background: "rgba(250,250,247,0.85)", backdropFilter: "blur(8px)" }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <a href="/blog" className="inline-flex items-center gap-2 text-sm font-semibold" data-testid="blog-questions-back">
            <span
              className="w-8 h-8 rounded-full grid place-items-center"
              style={{ background: "linear-gradient(135deg,#0F766E,#134E4A)" }}
            >
              <MessageCircle className="w-4 h-4 text-white" />
            </span>
            <span style={{ fontFamily: "'Lora', serif", fontWeight: 700 }}>
              SocialPulse<sup className="text-[9px]">®</sup> Blog · <span style={{ color: "#0F766E" }}>Questions</span>
            </span>
          </a>
          <nav className="hidden sm:flex items-center gap-4 text-sm text-slate-600">
            <a href="/blog" className="hover:text-slate-950">Home</a>
            <a href="/blog/topics" className="hover:text-slate-950">Topics</a>
            <a href="/blog/writers" className="hover:text-slate-950">Writers</a>
          </nav>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 md:px-10 py-10">
        <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mb-2">
          Ask the community
        </p>
        <h1 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Lora', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>
          Every question deserves a warm answer.
        </h1>
        <p className="mt-2 text-slate-600 text-lg max-w-2xl">
          Ask anything. Our AI assistant answers instantly and saves the reply so
          the next person to ask gets it free of charge. Popular questions rise.
        </p>

        {/* Ask card */}
        <form
          onSubmit={ask}
          className="mt-8 bg-white rounded-2xl border p-5 space-y-3"
          style={{ borderColor: "rgba(15,118,110,0.18)" }}
          data-testid="blog-questions-ask-form"
        >
          <div className="flex flex-wrap items-center gap-2">
            {TOPIC_CHIPS.slice(0, 8).map((t) => (
              <button
                key={t.slug || "all"}
                type="button"
                onClick={() => setTopic(t.slug)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  topic === t.slug
                    ? "text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                style={topic === t.slug ? { background: "#0F766E" } : {}}
                data-testid={`blog-questions-topic-${t.slug || "all"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">
              Your question
            </span>
            <TransliterateInput
              value={q}
              onChange={setQ}
              placeholder={`e.g. "What are the best months to visit Kerala?" or "How do I start a family tree?"`}
              className="w-full px-3 py-2.5 pr-10 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              defaultLang="off"
              data-testid="blog-questions-input"
              rows={3}
              maxLength={500}
              minLength={6}
            />
          </label>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Sparkles className="w-3 h-3" style={{ color: "#0F766E" }} />
              First-time answers are AI-generated. Repeat asks are free.
            </span>
            <button
              type="submit"
              disabled={asking || q.trim().length < 6}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-40"
              style={{ background: "#0F766E" }}
              data-testid="blog-questions-ask-submit"
            >
              {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Ask
            </button>
          </div>
          {askError && (
            <div className="text-xs text-red-600" data-testid="blog-questions-ask-error">{askError}</div>
          )}
        </form>

        {/* Fresh answer callout */}
        {freshAnswer && (
          <FreshAnswer answer={freshAnswer} onDismiss={() => setFreshAnswer(null)} />
        )}

        {/* List */}
        <section className="mt-12" data-testid="blog-questions-list">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
              {topic
                ? `Answers on ${TOPIC_CHIPS.find(t => t.slug === topic)?.label || topic}`
                : "Popular questions"}
            </h2>
            <div className="text-sm">
              <button
                type="button"
                onClick={() => setSort("popular")}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  sort === "popular" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
                data-testid="blog-questions-sort-popular"
              >
                Popular
              </button>
              <button
                type="button"
                onClick={() => setSort("recent")}
                className={`ml-1 px-3 py-1 rounded-full text-xs font-semibold ${
                  sort === "recent" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
                data-testid="blog-questions-sort-recent"
              >
                Recent
              </button>
            </div>
          </div>

          {loading && list.length === 0 && (
            <div className="flex items-center justify-center py-16 gap-2 text-slate-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading…
            </div>
          )}

          {!loading && list.length === 0 && (
            <div className="text-center py-14">
              <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                No questions yet on this topic. Be the first to ask — every question you
                add becomes a permanent resource for others.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {list.map((n) => <QuestionCard key={n.id} q={n} onUpvote={upvote} />)}
          </div>
        </section>

        <div className="mt-14 text-center">
          <button
            onClick={() => navigate("/publisher/write")}
            className="inline-flex items-center gap-1 text-sm font-semibold"
            style={{ color: "#0F766E" }}
            data-testid="blog-questions-to-write"
          >
            Ready to answer at length? Write an article <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </main>
    </div>
  );
}


function FreshAnswer({ answer, onDismiss }) {
  return (
    <div
      className="mt-4 rounded-2xl border p-5 relative"
      style={{ borderColor: "rgba(15,118,110,0.25)", background: "linear-gradient(135deg,#F0FDFA,#FFFFFF)" }}
      data-testid="blog-questions-fresh-answer"
    >
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-3 right-3 w-7 h-7 rounded-full grid place-items-center hover:bg-white"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-slate-400" />
      </button>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold" style={{ color: "#0F766E" }}>
        <Sparkles className="w-3.5 h-3.5" />
        {answer.cached ? "Cached answer" : "AI answer"}
        {answer.cached && (
          <span className="text-slate-400 lowercase tracking-normal font-normal">
            · saved from an earlier ask
          </span>
        )}
      </div>
      <div className="mt-2 text-slate-900 text-lg" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
        {answer.question}
      </div>
      <div className="mt-3 text-slate-700 text-sm leading-relaxed whitespace-pre-line">
        {answer.answer}
      </div>
    </div>
  );
}


function QuestionCard({ q, onUpvote }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);   // fetched-on-expand full record w/ related_articles
  const [loadingDetail, setLoadingDetail] = useState(false);

  const toggle = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (!detail) {
      setLoadingDetail(true);
      try {
        const r = await fetch(`${BASE}/questions/${q.id}`);
        if (r.ok) {
          const d = await r.json();
          setDetail(d);
        }
      } catch { /* noop */ }
      setLoadingDetail(false);
    }
  };

  const turnIntoArticle = (e) => {
    e.stopPropagation();
    const params = new URLSearchParams();
    params.set("title", q.question);
    params.set("draft", q.answer);
    if (q.topics?.[0]) params.set("topic", q.topics[0]);
    params.set("source", "question");
    params.set("source_id", q.id);
    navigate(`/publisher/write?${params.toString()}`);
  };

  const related = detail?.related_articles || [];

  return (
    <div
      className="bg-white rounded-2xl border p-4 flex gap-3"
      style={{ borderColor: "rgba(15,23,42,0.06)" }}
      data-testid={`blog-questions-card-${q.id}`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onUpvote(q); }}
        className="flex flex-col items-center justify-center rounded-lg border px-2.5 py-1.5 hover:bg-slate-50 shrink-0"
        style={{ borderColor: "rgba(15,23,42,0.1)" }}
        aria-label="Upvote question"
        data-testid={`blog-questions-upvote-${q.id}`}
      >
        <ThumbsUp className="w-3.5 h-3.5" style={{ color: "#0F766E" }} />
        <span className="text-[11px] font-bold mt-0.5">{q.helpful_votes ?? 0}</span>
      </button>
      <div className="flex-1 min-w-0">
        <button type="button" onClick={toggle} className="text-left w-full">
          <div className="text-slate-900 leading-snug" style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "1.1rem" }}>
            {q.question}
          </div>
          {!expanded && (
            <div className="mt-1 text-sm text-slate-500 line-clamp-2">
              {q.answer}
            </div>
          )}
        </button>
        {expanded && (
          <>
            <div className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-line">
              {q.answer}
            </div>

            {/* Listen — TTS button (idle → loading → native audio player) */}
            <ListenToAnswer qid={q.id} />

            {/* Related articles */}
            {loadingDetail && (
              <div className="mt-3 text-xs text-slate-400 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading related reads…
              </div>
            )}
            {!loadingDetail && related.length > 0 && (
              <div className="mt-4 pt-3 border-t" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid={`blog-questions-related-${q.id}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                  Deeper reads
                </div>
                <div className="space-y-2">
                  {related.map((a) => (
                    <a
                      key={a.id}
                      href={`/blog/${a.slug}`}
                      className="flex items-start gap-3 rounded-lg p-2 -m-2 hover:bg-slate-50 transition-colors"
                      data-testid={`blog-questions-related-article-${a.slug}`}
                    >
                      {a.cover_image_url && (
                        <img src={a.cover_image_url} alt="" className="w-16 h-16 rounded object-cover shrink-0" loading="lazy" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 line-clamp-2">
                          {a.title}
                        </div>
                        {a.summary && (
                          <div className="mt-0.5 text-xs text-slate-500 line-clamp-1">
                            {a.summary}
                          </div>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-300 mt-1 shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Turn-into-article CTA */}
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={turnIntoArticle}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border hover:bg-slate-50 transition-colors"
                style={{ borderColor: "rgba(15,118,110,0.35)", color: "#0F766E" }}
                data-testid={`blog-questions-turn-into-article-${q.id}`}
              >
                <PenLine className="w-3.5 h-3.5" /> Turn this answer into a full article ›
              </button>
            </div>
          </>
        )}
        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400">
          {q.topics?.length > 0 && (
            <span className="uppercase tracking-widest font-semibold">
              {q.topics.join(" · ")}
            </span>
          )}
          {typeof q.seen_count === "number" && <span>· {q.seen_count} views</span>}
        </div>
      </div>
    </div>
  );
}
